import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";

const CAT = {
  loading_unloading: "Loading & Unloading",
  daily_labour: "Daily Labour",
  tiles_work: "Tiles Work",
  house_repair: "House Repair",
};

const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function Forecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/forecast", { params: { days: d } });
      setData(r.data);
    } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, []);

  if (loading && !data) return <div className="text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin"/>Computing forecast…</div>;
  if (!data) return <div className="text-slate-400">No data.</div>;

  // group rows by category and by date for heatmap
  const byCatDate = {}; // {cat: {date: {hour: val}}}
  for (const r of data.rows) {
    byCatDate[r.category] ??= {};
    byCatDate[r.category][r.date] ??= {};
    byCatDate[r.category][r.date][r.hour] = r.predicted_workers;
  }
  const dates = Array.from(new Set(data.rows.map(r => r.date))).sort();
  const hours = Array.from({length: 15}, (_, i) => i + 7);
  const maxVal = Math.max(1, ...data.rows.map(r => r.predicted_workers));
  const cell = (v) => {
    if (!v) return "bg-slate-900/40";
    const t = v / maxVal;
    if (t > 0.8) return "bg-orange-600/90";
    if (t > 0.6) return "bg-orange-500/70";
    if (t > 0.4) return "bg-orange-400/50";
    if (t > 0.2) return "bg-orange-300/30";
    return "bg-orange-200/20";
  };

  const undersupply = data.top_slots.filter(s => s.predicted_workers > (data.available_supply[s.category] || 0));

  return (
    <div data-testid="admin-forecast">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><TrendingUp className="text-orange-400"/>Demand Forecast</h1>
        <div className="flex gap-2 items-center">
          <span className="text-xs text-slate-500">Horizon:</span>
          {[1,7,14,30].map(d => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} className={days === d ? "bg-orange-500 hover:bg-orange-600" : "border-slate-700 text-slate-300"} onClick={() => { setDays(d); load(d); }} data-testid={`horizon-${d}`}>{d}d</Button>
          ))}
        </div>
      </div>

      <Card className="admin-card p-5 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-500/20 text-orange-400 flex items-center justify-center shrink-0"><Sparkles size={18}/></div>
          <div className="flex-1 text-sm">
            <div className="font-bold text-slate-200 mb-1">AI Briefing (GPT via Emergent LLM Key)</div>
            {data.narrative ? (
              <div className="text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid="forecast-narrative">{data.narrative}</div>
            ) : (
              <div className="text-slate-500 italic">AI narrative not available (model offline). Using heuristic forecast only.</div>
            )}
          </div>
        </div>
      </Card>

      {undersupply.length > 0 && (
        <Card className="admin-card border-red-500/50 p-4 mb-4" data-testid="undersupply-alert">
          <div className="flex items-start gap-2 text-red-300">
            <AlertTriangle size={18} className="mt-0.5 shrink-0"/>
            <div className="text-sm">
              <div className="font-bold">Undersupply alert — pre-position workers</div>
              <ul className="mt-1 space-y-0.5 text-xs text-red-200/90">
                {undersupply.map((s, i) => (
                  <li key={i}>
                    {s.date} at {s.hour}:00 — predicted <b>{s.predicted_workers}</b> {CAT[s.category]} workers but only <b>{data.available_supply[s.category] || 0}</b> currently available.
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-4 gap-3 mb-5">
        {Object.entries(data.available_supply).map(([cat, n]) => (
          <Card key={cat} className="admin-card p-4" data-testid={`supply-${cat}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Available Supply</div>
            <div className="text-sm text-slate-400">{CAT[cat]}</div>
            <div className="text-3xl font-mono font-bold text-emerald-400 mt-1">{n}</div>
          </Card>
        ))}
      </div>

      <Card className="admin-card p-5 mb-4">
        <div className="font-bold text-slate-200 mb-3">Top Predicted Slots</div>
        <div className="divide-y divide-slate-800">
          {data.top_slots.map((s, i) => (
            <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`top-slot-${i}`}>
              <div>
                <div className="font-mono text-slate-200">{s.date} <span className="text-slate-500">· {WEEKDAYS[s.weekday]} · {s.hour.toString().padStart(2,"0")}:00</span></div>
                <div className="text-xs text-slate-500">{CAT[s.category]}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">history {s.historical_bookings}</span>
                <span className="font-mono text-xl font-bold text-orange-400">{s.predicted_workers}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {Object.entries(byCatDate).map(([cat, dateHours]) => (
        <Card key={cat} className="admin-card p-5 mb-3 overflow-x-auto">
          <div className="font-bold mb-3 text-slate-200">{CAT[cat]} — hourly demand heatmap</div>
          <table className="text-xs min-w-[720px]">
            <thead>
              <tr><th className="text-slate-500 font-mono text-left pr-3">Hour</th>{dates.map(d => <th key={d} className="text-slate-500 font-mono px-1 text-xs">{d.slice(5)}</th>)}</tr>
            </thead>
            <tbody>
              {hours.map(h => (
                <tr key={h}>
                  <td className="text-slate-500 font-mono pr-3">{h.toString().padStart(2,"0")}:00</td>
                  {dates.map(d => {
                    const v = dateHours[d]?.[h] || 0;
                    return <td key={d} className="p-0.5"><div className={`h-6 w-10 rounded ${cell(v)} flex items-center justify-center text-[10px] font-mono text-slate-100`}>{v || ""}</div></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      <p className="text-xs text-slate-500">Based on {data.history_bookings} historical bookings. Forecasts are predictions — actuals may vary.</p>
    </div>
  );
}
